import _, { Dictionary } from 'lodash'
import type { ChartData } from 'chart.js'
import { ExamStatistics } from '../../types'

export function getAbittiExamsChartDataCounts(
  examStatistics: ExamStatistics[],
  metric: 'exam_count' | 'answer_paper_count',
): ChartData<'line'> {
  return getAbittiExamsChartData(examStatistics, metric, getDataCounts)
}

export function getAbittiExamsChartDataPercentages(
  examStatistics: ExamStatistics[],
  metric: 'exam_count' | 'answer_paper_count',
): ChartData<'line'> {
  return getAbittiExamsChartData(examStatistics, metric, getDataPercentages)
}

function getAbittiExamsChartData(
  examStatistics: ExamStatistics[],
  metric: 'exam_count' | 'answer_paper_count',
  getData: (
    weeks: string[],
    metric: 'exam_count' | 'answer_paper_count',
    rowsByWeek: Dictionary<ExamStatistics>,
    examStatistics: ExamStatistics[],
  ) => number[],
): ChartData<'line'> {
  const weeks = _.uniq(examStatistics.map((row) => row.week))
  const rowsByServerVersion = _.groupBy(examStatistics, 'held_exam_ktp_version')
  const versionsOldestToNewest =
    Object.keys(rowsByServerVersion).sort(byVersion)

  return {
    labels: weeks,
    datasets: versionsOldestToNewest.map((version, index) => {
      const rowsByWeek = _.keyBy(rowsByServerVersion[version], 'week')
      const color = colorForRank(index, versionsOldestToNewest.length)
      return {
        label: version,
        data: getData(weeks, metric, rowsByWeek, examStatistics),
        borderColor: color,
        backgroundColor: color,
        tension: 0.2,
      }
    }),
  }
}

function getDataCounts(
  weeks: string[],
  metric: 'exam_count' | 'answer_paper_count',
  rowsByWeek: Dictionary<ExamStatistics>,
) {
  return weeks.map((week) => rowsByWeek[week]?.[metric] ?? 0)
}

function getDataPercentages(
  weeks: string[],
  metric: 'exam_count' | 'answer_paper_count',
  rowsByWeek: Dictionary<ExamStatistics>,
  examStatistics: ExamStatistics[],
) {
  return weeks.map((week) => {
    const weeklyTotals = _.mapValues(
      _.groupBy(examStatistics, 'week'),
      (rows) => _.sumBy(rows, metric),
    )
    const value = rowsByWeek[week]?.[metric] ?? 0
    const total = weeklyTotals[week]
    return total > 0 ? (value / total) * 100 : 0
  })
}

// Pick a color by a version's age rank: oldest (rank 0) -> first/cold color, newest
// (rank total-1) -> last/warm color, the rest spread evenly across the ramp.
function colorForRank(rank: number, total: number): string {
  const lastColor = OLD_TO_NEW_SERVER_COLORS.length - 1
  if (total <= 1) return OLD_TO_NEW_SERVER_COLORS[lastColor]
  const positionOnRamp = rank / (total - 1) // 0 = oldest .. 1 = newest

  // Stretch that 0..1 position across the color indexes (0..lastColor), snap to one.
  const slot = Math.round(positionOnRamp * lastColor)
  return OLD_TO_NEW_SERVER_COLORS[slot]
}

const OLD_TO_NEW_SERVER_COLORS = [
  '#0d0887',
  '#41049d',
  '#6a00a8',
  '#8f0da4',
  '#b12a90',
  '#cb4679',
  '#e16462',
  '#f2844b',
  '#fca636',
  '#fcce25',
]

const byVersion = new Intl.Collator('en', { numeric: true }).compare
